import { collection, findSelector, member, resource } from "data/helpers/apiv3";
import { Experience } from "data/records";
import { Map } from "immutable";
import { request } from "lib/apiv3/utils/request";
import { loadAllPages } from "lib/loadAllPages";
import { fulfilled, rejected } from "lib/promiseMiddlewareHelpers";

const [api, actions] = resource(Experience, [
  ["create", collection.post("", ["experience", "noRedirect"])],
  ["read", member.get("", ["appCode"])],
  ["list", collection.get("", ["clubId", "includeStats"])],
  member.post("copy", ["copyAllRoutes"]),
  member.post("disable"),
  member.post("publish"),
  member.post("removeRoute", ["routeId"]),
  member.get("lastPackage"),
  member.get("organization"),
  member.get("appCode", ["appCode"]),
  member.get("routesExport", ["appCode"]),
  ["generateAppCode", member.patch("appCode", { generate: true }, ["guideCode"])],
  member.patch("updateCode", ["code", "guideCode"]),
  member.get("stats"),
  member.get("appCodeUses"),
  member.post("expireGuest", ["appCodeUseId"]),
  member.post("unexpireGuest", ["appCodeUseId"]),
  member.get("guidePages"),
]);
actions.loadAllExperiences = (clubId, params) => (dispatch) => {
  const loadExperiencesPage = ({ pageIndex: page, perPage }) =>
    dispatch(actions.list({ clubId, page, perPage, ...params }));
  const isLastPage = ({ perPage, pageIndex, pageResults }) => {
    return perPage * (pageIndex + 1) >= pageResults.value.totalCount;
  };

  return loadAllPages({
    promiseProducer: loadExperiencesPage,
    perPage: 100,
    isLastPage,
  });
};

// TODO: remove this definition once https://github.com/ridewithgps/ridewithgps/pull/6483 is merged!
api.stats = (id) =>
  request.get(`/experiences/${id}/stats`).then((response) => {
    if (response.stats) {
      return { stats: response.stats };
    } else {
      return { stats: response };
    }
  });

const selectors = {
  ...findSelector(Experience),
  appCode: (state, id) => state.apiv3.experience.appCode.get(id),
  guideCode: (state, id) => state.apiv3.experience.guideCode.get(id),
  lastPackage: (state, id) => state.apiv3.experience.lastPackage.get(id),
  hasUpdates: (state, id) => state.apiv3.experience.hasUpdates.get(id),
  allowNoExpire: (state, id) => state.apiv3.experience.allowNoExpire.get(id),
  allowMessaging: (state, id) => state.apiv3.experience.allowMessaging.get(id),
  hasGuideShortcut: (state, id) => state.apiv3.experience.guideShortcut.get(id),
  byClubId: (state, clubId) => state.apiv3.experience.id.filter((e) => e.clubId === clubId),
};

const initialState = {
  appCode: Map(), // experienceId -> AppCode
  guideCode: Map(), // experienceId -> AppCode
  lastPackage: Map(), // experienceId -> ExperiencePackage
  hasUpdates: Map(), // experienceId -> Bool
  allowNoExpire: Map(), // experienceId -> Bool
  allowMessaging: Map(), // experienceId -> Bool
  guideShortcut: Map(), // experienceId -> Bool
};

/*
You can specify as many custom reducers as you need, as a map from the action type to a (state,action) => state
method.  The only difference is the state is going to be your slice of state, so you receive
state.apiv3.experience in this case, not the root state.  It's similar to combineReducers
*/
const reducers = {
  [fulfilled(actions.read.type)]: (state, action) => {
    const { experience, permissions, extras } = action.payload;
    const appCode = extras.find((e) => e.type === "app_code")?.app_code; // eslint-disable-line
    return state
      .setIn(["allowNoExpire", experience.id], permissions.allowNoExpire)
      .setIn(["allowMessaging", experience.id], permissions.allowMessaging)
      .setIn(["guideShortcut", experience.id], permissions.guideShortcut)
      .setIn(["appCode", experience.id], appCode || state.appCode.get(experience.id));
  },
  [fulfilled(actions.appCode.type)]: (state, action) => {
    const { id } = action.meta;
    const { appCode } = action.payload;
    const guideCode = action.payload.extras[0] && action.payload.extras[0].app_code;
    const nextState = state.setIn(["appCode", id], appCode);
    return guideCode ? nextState.setIn(["guideCode", id], guideCode) : nextState;
  },
  [fulfilled(actions.updateCode.type)]: (state, action) => {
    const { id } = action.meta;
    const { appCode } = action.payload;
    const key = appCode.guideCode ? "guideCode" : "appCode";
    return state.setIn([key, id], appCode);
  },
  [fulfilled(actions.generateAppCode.type)]: (state, action) => {
    const { id } = action.meta;
    const { appCode } = action.payload;
    const key = appCode.guideCode ? "guideCode" : "appCode";
    return state.setIn([key, id], appCode);
  },
  [fulfilled(actions.publish.type)]: (state, action) => {
    const { id } = action.meta;
    const experiencePackage = action.payload.extras[0].experience_package;
    return state
      .setIn(["lastPackage", id], experiencePackage)
      .setIn(["hasUpdates", id], action.payload.meta.has_updates);
  },
  [fulfilled(actions.lastPackage.type)]: (state, action) => {
    const { experiencePackage } = action.payload;
    return state
      .setIn(["lastPackage", experiencePackage.experienceId], experiencePackage)
      .setIn(["hasUpdates", experiencePackage.experienceId], action.payload.meta.has_updates);
  },
  [rejected(actions.lastPackage.type)]: (state, action) => {
    const { id } = action.meta;
    return state.setIn(["lastPackage", id], null).setIn(["hasUpdates", id], true);
  },
};

export {
  api as experienceApi,
  actions as experienceActions,
  selectors as experienceSelectors,
  reducers as experienceReducers,
  initialState as experienceInitialState,
};
